import { useAudio } from '@providers'
import classNames from 'classnames'
import { motion } from 'framer-motion'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { QuestShortDetails } from '@store'

import { CircularText } from '../CircularText'
import { QuestLayoutConfig, QuestViewState } from '../types/QuestCircleTypes'
import { GlowingCircleWrapper } from './GlowingCircleWrapper'
import starSvg from './Star.svg'
import styles from './SubQuestCircle.module.scss'

const matrixEffectTimeout = 2000

interface Props {
  initialState: QuestViewState
  pageIndex: number
  layoutConfig: QuestLayoutConfig
  quest?: QuestShortDetails
  visible?: boolean
  icon?: string
  isAnimated?: boolean
}

export const SubQuestCircle = ({
  initialState,
  icon,
  visible,
  quest,
  isAnimated,
  pageIndex,
  layoutConfig,
}: Props) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const questRef = useRef<HTMLDivElement>(null)
  const [radius, setRadius] = useState('0rem')
  const [revealQuestPicture, setRevealQuestPicture] = useState(false)
  const [revealQuestTitle, setRevealQuestTitle] = useState(false)
  const { playSwitchOff } = useAudio()

  const rotationCompensation = (-90 * (pageIndex + 1)) % 360

  useEffect(() => {
    const updateRadius = () => {
      if (questRef.current) {
        const { width } = questRef.current.getBoundingClientRect()
        const calculatedRadius = `-${width * 1.35}rem`
        setRadius(calculatedRadius)
      }
    }

    updateRadius()
    window.addEventListener('resize', updateRadius)
    return () => window.removeEventListener('resize', updateRadius)
  }, [])

  useEffect(() => {
    setTimeout(() => {
      setRevealQuestTitle(true)
    }, 1500)

    if (
      initialState === QuestViewState.ShouldOpen ||
      initialState === QuestViewState.Showed
    ) {
      setTimeout(() => {
        setRevealQuestPicture(true)
      }, matrixEffectTimeout)
    }
  }, [])

  const navigateToQuest = useCallback(() => {
    if (quest?.slug && initialState !== QuestViewState.Closed) {
      playSwitchOff()
      navigate(`/quest/${quest.slug}`)
    }
  }, [quest?.slug, initialState])

  const isClickable = quest?.slug && initialState !== QuestViewState.Closed
  const isQuestCompleted = Boolean(quest?.isSolved)

  return (
    <motion.div
      key={`quest-${quest?.id || 'empty'}-${pageIndex}`}
      ref={questRef}
      className={classNames([
        styles.quest,
        { [styles.quest_animated]: isAnimated },
        { [styles.quest_clickable]: isClickable },
      ])}
      onClick={navigateToQuest}
      style={{
        ...layoutConfig.position,
        width: layoutConfig.imageSize.width || '100%',
        height: layoutConfig.imageSize.height || '100%',
        fontSize: layoutConfig.fontSize,
        cursor: isClickable ? 'pointer' : 'not-allowed',
      }}
      animate={{
        rotate: rotationCompensation,
        opacity: quest ? 1 : 0.3,
      }}
      transition={{
        duration: 0.3,
        ease: 'easeInOut',
      }}
      initial={{ opacity: 0 }}
    >
      {revealQuestPicture && (
        <motion.div
          className={styles.glow}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <GlowingCircleWrapper
            size={{
              width: layoutConfig.imageSize.width,
              height: layoutConfig.imageSize.height,
            }}
            isCompleted={isQuestCompleted}
          >
            <div
              className={classNames(styles.image)}
              style={{
                ...layoutConfig.imageSize,
              }}
            >
              <img src={icon || starSvg} className={styles.picture} />
            </div>
          </GlowingCircleWrapper>

          <div
            className={classNames([
              styles.title,
              visible ? styles.visible : '',
            ])}
          >
            <div
              className={styles.wrapperQuest}
              style={{
                ...layoutConfig.size,
              }}
            >
              <CircularText
                key={`circular-${quest?.id || 'empty'}-${pageIndex}`}
                offset={layoutConfig.textOffset}
                reveal={revealQuestTitle}
                text={t(`main.quests.${quest?.slug}`) || ''}
                radius={radius}
                angleStep={25}
                state={initialState}
                matrixEffectTimeout={matrixEffectTimeout}
              />
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  )
}
